import { Button, Tooltip } from '@ads/components-react';
import { PlusIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';

import { Alert } from '@/shared/ui/atoms';
import { useBulkLink } from '../../../hooks/useBulkLink';
import { useGradeConfig } from '../../../hooks/useGradeConfig';
import type { Activity } from '../../../types/gradeConfig';
import { ModuleDot } from '../../atoms/ModuleDot';
import { type ModuleFilterOption, ModuleFilterSelect } from '../../molecules/ModuleFilterSelect';
import { SectionCard } from '../../molecules/SectionCard';
import { LinkedDisciplineRow, ROW_GRID } from '../LinkedDisciplineRow';

/** Grupo de vínculos por módulo (ou "sem módulo"). */
interface Group {
  readonly key: string;
  readonly moduleIndex: number;
  readonly name: string;
  readonly stats: string;
  readonly showHeader: boolean;
  readonly rows: readonly Activity[];
}

/**
 * Card "Disciplinas vinculadas": tabela agrupada por módulo (grid Tailwind) com filtro
 * de módulo, botão "Vincular disciplinas" e empty state.
 */
export function LinkedDisciplinesTable() {
  const { t } = useTranslation('gradeAttendanceConfig');
  const { classGroup, modules, activities, filterModule, setFilterModule } = useGradeConfig();
  const { openModal } = useBulkLink();

  const withoutModule = activities.filter(a => !a.moduleId).length;
  // Sem módulos não há destino para o vínculo → ação bloqueada com tratamento visual.
  const noModules = !!classGroup && modules.length === 0;

  const moduleFilters: ModuleFilterOption[] = [
    { id: 'all', label: t('linked.filterAll'), count: activities.length },
    ...modules.map(m => ({
      id: m.id,
      label: m.name,
      count: activities.filter(a => a.moduleId === m.id).length,
    })),
    ...(withoutModule > 0
      ? [{ id: 'none', label: t('linked.filterNone'), count: withoutModule }]
      : []),
  ];

  function matchFilter(a: Activity): boolean {
    if (filterModule !== 'all') {
      const want = filterModule === 'none' ? null : filterModule;
      if ((a.moduleId ?? null) !== want) return false;
    }
    return true;
  }

  const showHeaders = filterModule === 'all' && modules.length > 0;
  const order: (string | null)[] = [...modules.map(m => m.id), null];

  const groups: Group[] = [];
  for (const mid of order) {
    const rows = activities.filter(a => (a.moduleId ?? null) === mid && matchFilter(a));
    if (rows.length === 0) continue;
    const moduleIndex = mid ? modules.findIndex(m => m.id === mid) : -1;
    const m = mid ? modules.find(x => x.id === mid) : undefined;
    const stats = m
      ? t('linked.groupStatsModule', { count: rows.length })
      : t('linked.groupStatsNoModule', { count: rows.length });
    groups.push({
      key: mid ?? 'none',
      moduleIndex,
      name: m?.name ?? t('linked.groupNoModule'),
      stats,
      showHeader: showHeaders,
      rows,
    });
  }

  const isEmpty = activities.length === 0;

  return (
    <SectionCard
      title={t('linked.title')}
      headerRight={
        <span className="text-ctx-content-base tabular-nums ts-caption-xxs">
          {t('linked.count', { count: activities.length })}
        </span>
      }
    >
      {noModules && (
        <div className="px-xxxs py-micro">
          <Alert type="warning" description={t('linked.noModulesWarning')} />
        </div>
      )}

      {/* Filtro por módulo + ações */}
      <div className="flex flex-wrap items-center justify-between gap-micro border-b-hairline border-ctx-layout-border px-xxxs py-micro">
        <div className="w-full sm:w-[50%]">
          <ModuleFilterSelect
            label={t('linked.moduleFilterLabel')}
            filters={moduleFilters}
            active={filterModule}
            onSelect={setFilterModule}
          />
        </div>
        {noModules ? (
          <Tooltip title={t('linked.noModulesTooltip')} side="top">
            <Button hierarchy="primary" size="sm" leadingIcon={PlusIcon} disabled>
              {t('linked.bulk')}
            </Button>
          </Tooltip>
        ) : (
          <Button
            hierarchy="primary"
            size="sm"
            leadingIcon={PlusIcon}
            onClick={openModal}
            disabled={!classGroup}
          >
            {t('linked.bulk')}
          </Button>
        )}
      </div>

      {isEmpty ? (
        <div className="flex flex-col items-center gap-nano px-xxxs py-xl text-center">
          <span className="text-ctx-content-title ts-heading-xs">{t('linked.emptyTitle')}</span>
          <span className="max-w-[28rem] text-ctx-content-placeholder ts-caption-xxs">
            {t('linked.emptyDesc')}
          </span>
          <Button
            hierarchy="primary"
            size="sm"
            onClick={openModal}
            disabled={!classGroup || noModules}
          >
            {t('linked.bulk')}
          </Button>
        </div>
      ) : (
        <div className="overflow-x-auto">
          {/* Cabeçalho de colunas */}
          <div
            className={`${ROW_GRID} border-b-hairline border-ctx-layout-border px-mini py-nano uppercase tracking-wide text-ctx-content-placeholder ts-caption-xxs`}
          >
            <span />
            <span>{t('linked.columns.tipo')}</span>
            <span>{t('linked.columns.item')}</span>
            <span>{t('linked.columns.controls')}</span>
            <span />
          </div>

          {groups.map(group => (
            <div key={group.key}>
              {group.showHeader && (
                <div className="flex items-center justify-between gap-mini border-y-hairline border-ctx-layout-border bg-ctx-layout-body px-xxxs py-nano">
                  <span className="flex items-center gap-nano">
                    <ModuleDot index={group.moduleIndex} />
                    <span className="text-ctx-content-title ts-subtitle-xxs">{group.name}</span>
                  </span>
                  <span className="shrink-0 text-ctx-content-base tabular-nums ts-caption-xxs">
                    {group.stats}
                  </span>
                </div>
              )}
              {group.rows.map(a => (
                <LinkedDisciplineRow key={a.id} activity={a} />
              ))}
            </div>
          ))}
        </div>
      )}
    </SectionCard>
  );
}
